import React, { Fragment, FunctionComponent, ReactNode, useCallback, useEffect, useMemo, useRef } from 'react'
import styles from './ObjectsPage.module.sass'
import { useNavigation, useRootDispatch, useRootSelector } from '../../../../utils/hooks'
import Field from '../../../controls/Field/Field'
import { refsActions } from '../../../../redux/pages/settings/refs'
import Spacer from '../../../etc/Spacer'
import Button, { ButtonStyle } from '../../../controls/Button/Button'
import { groupBy } from 'fp-ts/es6/NonEmptyArray'
import { RefIntersection } from '../../../../responses/refs/refs'
import { flow } from 'fp-ts/es6/function'
import { entries, unit } from '../../../../utils/utils'
import { ObjectCategory, ObjectType, WorkObject } from '../../../../responses/objects'
import Helmet from 'react-helmet'
import * as Array from 'fp-ts/es6/Array'
import * as Option from 'fp-ts/es6/Option'
import { pipe } from 'fp-ts/es6/pipeable'
import ObjectPanel from '../../../data-components/elements/ObjectPanel/ObjectPanel'
import clsx from 'clsx'
import { apiRequests } from '../../../../http/config'
import { fromQueryWithReset } from '../../../../utils/rx-operators'
import InfiniteScroll from '../../../etc/scroll/InfiniteScroll'
import EditIcon from 'files/edit.svg'
import DeleteIcon from 'files/delete.svg'
import SearchIcon from 'files/search.svg'
import PlusIcon from 'files/plus.svg'
import { concat, head } from 'fp-ts/NonEmptyArray'
import { Colors } from '../../../../styles'
import { useObservableState } from 'observable-hooks'
import Dropdown from '../../../data-components/dropdowns/Dropdown/Dropdown'

export const groupObjects = flow(
    groupBy((row: WorkObject) => row.category.name),
    entries,
)

const Type: FunctionComponent<{ type: ObjectType }> = ({type}) => <>
    <td className={clsx(styles.type, styles.bold)}>{type.name}</td>
    <td className={styles.type}>{type.cost}</td>
    <td className={styles.type}>{type.date_range}</td>
</>

const ObjectsPage: FunctionComponent = () => {
    const dispatch = useRootDispatch()
    const {category, search, data} = useRootSelector(state => ({
        data: state.pages.settings.refs.data['work-object'] || [],
        search: state.pages.settings.refs.search['work-object'] || '',
        category: state.pages.settings.refs.category,
    }))

    const grouped = useMemo(() => groupObjects(data), [data])

    const applyFilter = useCallback((data: { search?: string, category?: ObjectCategory | null }) => {
        dispatch(refsActions.request.init({
            key: 'work-object',
            search: (data.search === undefined) ? search : data.search,
            category: (data.category === undefined) ? category : data.category,
        }))
    }, [category, dispatch, search])

    useEffect(() => {
        applyFilter({})
    }, [applyFilter])

    const [categories, searchCategories] = useObservableState(fromQueryWithReset(query => apiRequests.dictionary.list({
        params: {key: 'work-object-category'},
        query
    })), [])

    const listRef = useRef(null)
    useNavigation(listRef)

    return <div className={styles.page}>
        <Helmet><title>Объекты</title></Helmet>
        <div className={styles.header}>
            <Field
                type={'text'}
                value={search}
                onChange={search => applyFilter({search})}
                placeholder={'Поиск'}
                rightItem={<SearchIcon/>}
            />
            <Dropdown
                className={styles.dropdown}
                data={concat<ObjectCategory | null>([null])(categories)}
                selected={unit(category)}
                content={(selected, render) => selected.map(render).join()}
                toKey={category => category? category.id: 'all'}
                render={category => category? category.name: 'Все категории'}
                onClick={category => applyFilter({category})}
                onChange={(search, reset) => searchCategories({search, reset})}
            />
            <Spacer/>
            <Button
                color={Colors.blue}
                buttonStyle={ButtonStyle.BORDERED}
                onClick={() => dispatch(refsActions.startCreate('work-object-category'))}
            >
                <PlusIcon/> Категория
            </Button>
            <Button
                color={Colors.blue}
                buttonStyle={ButtonStyle.BORDERED}
                onClick={() => dispatch(refsActions.startCreate('work-object'))}
            >
                <PlusIcon/> Объект
            </Button>
        </div>
        <InfiniteScroll
            ref={listRef}
            className={styles.body}
            onReach={() => dispatch(refsActions.request.more('work-object'))}
        >
            {
                grouped.map(([category, objects]) => <Fragment key={category}>
                    <div className={styles.category}>
                        <div>{category}</div>
                        <Spacer/>
                        <div className={styles.actions}>
                            <Button
                                title={'Редактировать категорию'}
                                onClick={() => dispatch(refsActions.startEdit({
                                    key: 'work-object-category',
                                    ref: head(objects).category as RefIntersection
                                }))}
                            >
                                <EditIcon/>
                            </Button>
                        </div>
                    </div>
                    <div className={styles.table}>
                        <table>
                            <thead>
                            <tr>
                                <th>Название</th>
                                <th>Папка</th>
                                <th>Тип</th>
                                <th>Стоимость</th>
                                <th>Дни</th>
                                <th/>
                            </tr>
                            </thead>
                            {
                                objects.map(object => <tbody key={object.id}>
                                <tr className={styles.object}>
                                    <td rowSpan={object.types.length || 1}>
                                        <ObjectPanel className={styles.info} object={object}/>
                                    </td>
                                    <td rowSpan={object.types.length || 1}>{object.folder}</td>
                                    {
                                        pipe(
                                            object.types,
                                            Array.head,
                                            Option.map(type => <Type key={type.id} type={type}/>),
                                            Option.getOrElse(() => <td colSpan={3}/>)
                                        )
                                    }
                                    <td rowSpan={object.types.length || 1}>
                                        <div className={styles.actions}>
                                            <Button
                                                onClick={() => dispatch(refsActions.startEdit({
                                                    key: 'work-object',
                                                    ref: object as RefIntersection,
                                                }))}
                                                title={'Редактировать'}
                                            >
                                                <EditIcon/>
                                            </Button>
                                            <Button
                                                onClick={() => dispatch(refsActions.startDelete({
                                                    key: 'work-object',
                                                    ref: object as RefIntersection,
                                                }))}
                                                title={'Удалить'}
                                            >
                                                <DeleteIcon/>
                                            </Button>
                                        </div>
                                    </td>
                                </tr>
                                {
                                    pipe(
                                        object.types,
                                        Array.tail,
                                        Option.map(Array.map(type => <tr key={type.id}>
                                            <Type type={type}/>
                                        </tr>)),
                                        Option.getOrElse<ReactNode>(() => undefined)
                                    )
                                }
                                </tbody>)
                            }
                        </table>
                    </div>
                </Fragment>)
            }
        </InfiniteScroll>
    </div>
}

export default ObjectsPage